import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { getRewardMembers, getRewardHistory, adjustPoints, saveRewardSettings } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Gift, RefreshCw } from "lucide-react";

const RULES = [
  { key: "checkin", label: "Per day checked in" },
  { key: "purchase_pct", label: "Back on every purchase (before tax)", unit: "%" },
  { key: "referral_pct", label: "Of what a referred friend pays (before tax)", unit: "%" },
  { key: "streak_points", label: "Food-logging streak bonus" },
  { key: "streak_days", label: "…every N days in a row", unit: "days" },
  { key: "early_renewal", label: "Renewing early" },
  { key: "early_renewal_days", label: "…at least N days before the end", unit: "days" },
  { key: "on_time_payment", label: "Annual instalment paid on time (incl. 3-day grace)" },
  { key: "max_discount_pct", label: "Most of a plan's price payable in points", unit: "%" },
  { key: "min_redeem_points", label: "Fewest points used at once" },
  { key: "expiry_months", label: "Points expire after this long with no activity: none earned or used (0 = never)", unit: "months" },
  { key: "expiry_reminder_days", label: "…members are reminded this many days before", unit: "days" },
];
const when = (iso) => new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export default function RewardPoints() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [data, setData] = useState(null);
  const [rules, setRules] = useState(null);
  const [member, setMember] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await getRewardMembers();
      setData(d);
      setRules(d.settings);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reward points");
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const saveRules = async () => {
    try {
      const saved = await saveRewardSettings(rules);
      setRules(saved);
      toast.success("Points rules saved for every gym");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save");
    }
  };

  if (!atLeast(effectiveRole || user?.role, "manager")) return <div className="card-surface p-8 text-sm text-zinc-500">Managers only.</div>;
  const members = (data?.members || []).filter((m) => !q || `${m.name} ${m.customer_key}`.toLowerCase().includes(q.toLowerCase()));
  const values = rules?.point_values || {};

  return (
    <div className="space-y-6" data-testid="reward-points-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Retention</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Reward Points</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Members earn points for turning up, referring friends, logging their food and renewing early, and spend
            them on renewals in the app. Points belong to the member, wherever they train.
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {rules && (
        <div className="card-surface p-5" data-testid="reward-rules">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-zinc-100">How points work (all gyms)</div>
            {isMaster ? <Button size="sm" onClick={saveRules} data-testid="reward-rules-save">Save rules</Button>
              : <span className="text-[11px] text-zinc-500">Only a Master Admin can change these</span>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {RULES.map((r) => (
              <label key={r.key} className="block space-y-1">
                <span className="text-[11px] text-zinc-400">{r.label}</span>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} value={rules[r.key]} readOnly={!isMaster}
                    onChange={(e) => setRules({ ...rules, [r.key]: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid={`reward-rule-${r.key}`} />
                  <span className="text-xs text-zinc-500">{r.unit || "pts"}</span>
                </div>
              </label>
            ))}
            {Object.keys(values).map((cur) => (
              <label key={cur} className="block space-y-1">
                <span className="text-[11px] text-zinc-400">1 point is worth ({cur})</span>
                <Input type="number" min={0} step="0.01" value={values[cur]} readOnly={!isMaster}
                  onChange={(e) => setRules({ ...rules, point_values: { ...values, [cur]: e.target.value === "" ? "" : Number(e.target.value) } })}
                  className="bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid={`reward-value-${cur}`} />
              </label>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-zinc-500">Points started counting on {rules.started_on}; nothing before that earns points.</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="card-surface overflow-x-auto">
          <div className="flex items-center justify-between gap-2 border-b border-[#1f1f1f] p-3">
            <div className="text-sm font-semibold text-zinc-100">Members at {activeFacility?.name || "this gym"}</div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="h-8 max-w-[200px] bg-[#0b0b0b] border-[#262626] text-zinc-100" />
          </div>
          {!data ? <div className="p-8 text-center text-sm text-zinc-500">Loading…</div> : members.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500"><Gift className="mx-auto mb-2 h-5 w-5 text-zinc-600" /> No points yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="reward-members">
              <thead>
                <tr className="border-b border-[#1f1f1f] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-4 py-3">Member</th><th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-right">Earned</th><th className="px-4 py-3">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.customer_key} className="cursor-pointer border-b border-[#161616] last:border-0 hover:bg-white/[0.02]"
                    onClick={() => setMember(m)} data-testid={`reward-member-${m.customer_key}`}>
                    <td className="px-4 py-3"><div className="font-medium text-zinc-100">{m.name}</div><div className="text-[11px] text-zinc-500">{m.customer_key}</div></td>
                    <td className="px-4 py-3 text-right font-semibold text-zinc-100">{m.balance.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-zinc-400">{m.earned.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{when(m.last_activity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card-surface p-4">
          <div className="mb-2 text-sm font-semibold text-zinc-100">Recent</div>
          <ul className="divide-y divide-[#161616] text-xs">
            {(data?.recent || []).map((r) => (
              <li key={r.id} className="flex justify-between gap-2 py-2">
                <span className="text-zinc-300">{r.name}<span className="text-zinc-500"> · {r.label}{r.note ? ` · ${r.note}` : ""}</span></span>
                <span className={r.delta > 0 ? "text-emerald-300" : "text-zinc-400"}>{r.delta > 0 ? "+" : ""}{r.delta}</span>
              </li>
            ))}
            {data && !data.recent.length && <li className="py-2 text-zinc-500">Nothing yet.</li>}
          </ul>
        </div>
      </div>

      {member && <MemberDialog member={member} onClose={() => setMember(null)} onChanged={load} />}
    </div>
  );
}

function MemberDialog({ member, onClose, onChanged }) {
  const [hist, setHist] = useState(null);
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const key = member.customer_key;

  const load = useCallback(() => getRewardHistory(key).then(setHist, () => setHist({ balance: member.balance, history: [] })), [key, member.balance]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await adjustPoints({ customer_key: key, delta: Number(delta), note });
      toast.success("Points updated");
      setDelta(""); setNote("");
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#0f0f0f] border-[#262626] text-zinc-100" data-testid="reward-member-dialog">
        <DialogHeader>
          <DialogTitle>{member.name} · {hist ? hist.balance.toLocaleString() : member.balance.toLocaleString()} points</DialogTitle>
          <DialogDescription className="text-zinc-400">Every change, with why. Adjustments are logged in the audit log.</DialogDescription>
        </DialogHeader>
        <ul className="max-h-64 divide-y divide-[#161616] overflow-y-auto text-xs">
          {(hist?.history || []).map((r) => (
            <li key={r.id} className="flex justify-between gap-2 py-2">
              <span className="text-zinc-300">{r.label}{r.note ? <span className="text-zinc-500"> · {r.note}</span> : null}
                <div className="text-[10px] text-zinc-600">{when(r.created_at)}</div></span>
              <span className={r.delta > 0 ? "text-emerald-300" : "text-zinc-400"}>{r.delta > 0 ? "+" : ""}{r.delta}</span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-[110px_1fr] gap-2">
          <Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+100 or −50"
            className="bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid="reward-adjust-delta" />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (e.g. goodwill, correction)"
            className="bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid="reward-adjust-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy || !Number(delta) || note.trim().length < 3} data-testid="reward-adjust-save">Add / remove points</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
